import { useState, type FormEvent } from 'react';
import KakaoTalkButton from '@/components/feature/KakaoTalkButton';

const CONSULT_FORM_URL = 'https://readdy.ai/api/form/d8ro6tdcg4qspo125t30';

const insuranceTypes = [
  {
    icon: 'ri-user-heart-line',
    title: '가족간병보험',
    desc: '가족이 직접 간병하면 보험사에서 일당을 지급받을 수 있습니다. 코리아케어러스 등록으로 자격을 인정받으세요.',
    highlight: '일 최대 15만원',
  },
  {
    icon: 'ri-nurse-line',
    title: '간병인 사용 일당 보험',
    desc: '전문 간병인을 고용했을 때 지급되는 보험금입니다. 간병인 매칭부터 서류 발급까지 도와드립니다.',
    highlight: '서류 발급 완벽 지원',
  },
  {
    icon: 'ri-shield-star-line',
    title: '장기간병보험 (LTC)',
    desc: '장기요양등급 판정을 받은 어르신을 위한 보험입니다. 등급 신청부터 보험 청구까지 함께합니다.',
    highlight: '등급 신청 상담 가능',
  },
];

export default function InsuranceConsultation() {
  const [form, setForm] = useState({ name: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.name.trim() || !form.phone.trim()) {
      setError('성함과 연락처를 모두 입력해주세요.');
      return;
    }
    if (!agreed) {
      setError('개인정보 수집 및 이용에 동의해주세요.');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('name', form.name);
      params.append('phone', form.phone);

      await fetch(CONSULT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setDone(true);
    } catch {
      setError('전송 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
    setLoading(false);
  };

  const inputClass = 'w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#2E7D6B] focus:ring-1 focus:ring-[#2E7D6B] transition-colors bg-white placeholder-gray-300';

  return (
    <section id="insurance-consultation" className="w-full py-16 md:py-20 scroll-mt-20" style={{ backgroundColor: '#f9fdfb' }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: '#2E7D6B' }}>INSURANCE CONSULTATION</p>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            간병보험, 복잡하게 생각하지 마세요
          </h2>
          <p className="text-sm text-gray-500 max-w-2xl mx-auto leading-relaxed">
            어떤 보험에 가입되어 있는지, 얼마나 받을 수 있는지 — 코리아케어러스 전문 상담사가 꼼꼼하게 확인해 드립니다.
            <strong className="text-gray-700"> 무료 상담</strong>이니 부담 없이 문의하세요.
          </p>
        </div>

        {/* Main Content: Insurance Types + Form */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left: Insurance type cards */}
          <div className="lg:col-span-3 space-y-4">
            {insuranceTypes.map((ins, idx) => (
              <div
                key={idx}
                className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-gray-100 hover:border-[#2E7D6B]/20 transition-all group cursor-default"
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                  style={{ backgroundColor: '#e8f5f1' }}
                >
                  <i className={`${ins.icon} text-lg`} style={{ color: '#2E7D6B' }}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-bold text-gray-900">{ins.title}</h3>
                    <span
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
                      style={{ backgroundColor: 'rgba(46,125,107,0.08)', color: '#2E7D6B' }}
                    >
                      {ins.highlight}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">{ins.desc}</p>
                </div>
                <div className="flex-shrink-0 hidden sm:flex items-center">
                  <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-[#2E7D6B] transition-colors"></i>
                </div>
              </div>
            ))}

            {/* CTA Buttons below insurance cards */}
            <div className="flex flex-wrap gap-3 pt-2">
              <a
                href="tel:01091145914"
                className="flex-1 min-w-[140px] py-3.5 rounded-xl text-sm font-bold text-center whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                style={{ backgroundColor: '#2E7D6B', color: '#fff' }}
              >
                <i className="ri-phone-line"></i>전화 상담
              </a>
              <KakaoTalkButton
                className="flex-1 min-w-[140px] py-3.5 rounded-xl text-sm font-bold text-center whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                style={{ backgroundColor: '#FEE500', color: '#391B1B' }}
                ariaLabel="간병보험 카톡 상담"
              >
                <i className="ri-kakao-talk-fill"></i>카톡 상담
              </KakaoTalkButton>
            </div>
          </div>

          {/* Right: Consultation Form */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl bg-white border border-gray-100 p-6 sticky top-24">
              {done ? (
                /* Success state */
                <div className="text-center py-8">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e8f5f1' }}>
                    <i className="ri-check-line text-2xl" style={{ color: '#2E7D6B' }}></i>
                  </div>
                  <h4 className="text-base font-bold text-gray-900 mb-1">상담 신청 완료!</h4>
                  <p className="text-xs text-gray-500 leading-relaxed mb-5">
                    입력하신 연락처로 전문 상담사가<br />빠른 시일 내에 연락드리겠습니다.
                  </p>
                  <button
                    onClick={() => {
                      setDone(false);
                      setForm({ name: '', phone: '' });
                      setAgreed(false);
                    }}
                    className="px-5 py-2.5 rounded-xl text-xs font-bold cursor-pointer hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: '#e8f5f1', color: '#2E7D6B' }}
                  >
                    추가 상담 신청하기
                  </button>
                </div>
              ) : (
                /* Form */
                <form onSubmit={handleSubmit} data-readdy-form="">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e8f5f1' }}>
                      <i className="ri-chat-check-line text-sm" style={{ color: '#2E7D6B' }}></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-900">무료 상담 신청</h4>
                      <p className="text-[10px] text-gray-400">전문 상담사가 친절히 안내해 드립니다</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        성함 <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="홍길동"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        연락처 <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="010-0000-0000"
                        className={inputClass}
                      />
                    </div>

                    {/* Agreement */}
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        className="w-4 h-4 rounded-md border-gray-300 text-[#2E7D6B] focus:ring-[#2E7D6B] mt-0.5 shrink-0 cursor-pointer"
                      />
                      <span className="text-[11px] text-gray-400 leading-relaxed">
                        개인정보 수집 및 이용에 동의합니다. 입력된 정보는 상담 목적으로만 사용됩니다.
                      </span>
                    </label>

                    {error && (
                      <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-xl">
                        <i className="ri-error-warning-line text-red-400 text-sm" />
                        <p className="text-xs text-red-600">{error}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3.5 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: '#2E7D6B' }}
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>신청 중...
                        </span>
                      ) : (
                        '무료 상담 신청하기'
                      )}
                    </button>

                    <p className="text-[10px] text-gray-400 text-center leading-relaxed">
                      상담은 무료이며, 부담 없이 문의하세요<br />
                      평일/주말 오전 9시 ~ 오후 11시
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Bottom highlight banner */}
        <div className="mt-10 p-5 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center gap-4" style={{ backgroundColor: '#fff', border: '1px solid rgba(46,125,107,0.15)' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#e8f5f1' }}>
            <i className="ri-lightbulb-line text-lg" style={{ color: '#2E7D6B' }}></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900 mb-0.5">이미 간병 중이신가요?</p>
            <p className="text-xs text-gray-500 leading-relaxed">
              현재 간병 중이시라면 지금 바로 <strong className="text-gray-700">보험 청구 서류 발급</strong>이 가능합니다. 간병이 끝나기 전에 미리 준비하세요!
            </p>
          </div>
          <a
            href="/app/insurance-doc"
            className="px-5 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity flex-shrink-0 flex items-center gap-2"
            style={{ backgroundColor: '#0d2b24', color: '#6fcfb8' }}
          >
            <i className="ri-file-list-3-line"></i>보험청구 가이드 보기
          </a>
        </div>
      </div>
    </section>
  );
}